import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { firstname } from 'app/helpers/names';
import { lsrNavigation } from 'app/helpers/navigation';
import { ExposedAuthRequestReq } from 'app/interfaces/requests/Exposed/exposedAuthRequestReq';
import { ExposedCreateUserReq } from 'app/interfaces/requests/Exposed/exposedCreateUserReq';
import { ExposedUpdateUserReq } from 'app/interfaces/requests/Exposed/exposedUpdateUserReq';
import { ExposedAccountsLoginRes } from 'app/interfaces/responses/Exposed/exposedAccountsLoginRes';
import { ExposedAuthRequestRes } from 'app/interfaces/responses/Exposed/exposedAuthRequestRes';
import { ExposedListEmployeeDivisionsRes } from 'app/interfaces/responses/Exposed/exposedListEmployeeDivisionsRes';
import { ExposedListRolesRes } from 'app/interfaces/responses/Exposed/exposedListRolesRes';
import { ExposedListUsersRes } from 'app/interfaces/responses/Exposed/exposedListUsersRes';
import { ExposedReadListUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadListUserDetails';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListAudkenningarLogsRes } from 'app/interfaces/responses/List/listAudkenningarLogsRes';
import { ListPagedRes } from 'app/interfaces/responses/List/listPagedRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, finalize, map } from 'rxjs';
import { LSRNavigationService } from '../lsrnavigation/lsrnavigation.service';
import { TranslationService } from '../translation/translation.service';
declare var $: any;

@Injectable({
    providedIn: 'root',
})
export class LsrauthService {
    private httpRequestHelper = new HttpRequestHelper();

    navigation: FuseNavigationItem[];

    // Logged in user details (local)
    private bhUserDetails = new BehaviorSubject<ExposedReadUserDetailsRes>(null);
    userDetails = this.bhUserDetails.asObservable();

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        public dialog: MatDialog,
        private lsrNavigationService: LSRNavigationService,
        private translationService: TranslationService
    ) {
        this.navigation = lsrNavigation;

        this.translationService.translationsChanged$.subscribe({
            next: () => {
                for (let item of this.navigation) {
                    this.setTitleAndSubtitle(item);
                }
            },
        });
    }

    changeUserDetails(user: ExposedReadUserDetailsRes) {
        this.bhUserDetails.next(user);
    }

    public fetchUserDetails(showLoader = false): Observable<ExposedReadUserDetailsRes> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage('Sæki upplýsingar');
        }

        let subject = new Subject<ExposedReadUserDetailsRes>();
        this.http
            .get(environment.localApi + 'Auth/claimDetails', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadUserDetailsRes>) => {
                    let userDetails = res.returnItem;
                    this.changeUserDetails(userDetails);

                    for (let i = 0; i < this.navigation.length; i++) {
                        this.setAccess(userDetails, this.navigation[i]);
                    }

                    // Updates the navigation
                    this.lsrNavigationService.changeNavigation(this.navigation);

                    subject.next(userDetails);
                },
                (error) => {
                    console.log(error);
                }
            )
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    // Determines if a navigation item should be enabled in the menu
    // Steps:
    //   1. Checks if the user has access to the current item based on its roles, access checkers and parent access
    //   2. Show and enable the item if the user has access to it
    //     a. Employees are shown all menu items
    //   3. Check the access for all children of the item (recursively calls this function for child items)
    //     a. If the current item has no roles or access checkers, it is enabled if the user has access to any child item
    setAccess(userDetails: ExposedReadUserDetailsRes, item: FuseNavigationItem, parentAccess: boolean = true) {
        item.meta ??= {};
        let hasAccess = this.checkHasAccess(userDetails, item, parentAccess);

        // Employess can see all navigation items even if they don't have the necessary role to access the function
        item.meta.hide = !(hasAccess || this.inRole(userDetails, USER_ROLES.EMPLOYEE_LSR));
        item.disabled = !hasAccess;

        if (item.children && item.children.length > 0) {
            for (let i = 0; i < item.children.length; i++) {
                let hasChildAccess = this.setAccess(userDetails, item.children[i], hasAccess);
                // Update the navigation item if the user has access to some child navigation item
                if (!item.meta?.roles && !item.meta?.additionalAccessCheckers && hasChildAccess) {
                    item.meta.hide = false;
                    item.disabled = false;
                    hasAccess = true;
                }
            }
        }

        return hasAccess;
    }

    setTitleAndSubtitle(item: FuseNavigationItem) {
        const key = 'label_nav_' + item.id;
        const translated = this.translationService.translate(key);

        item.title = translated && translated !== key ? translated : (item.title ?? key);

        const infoKey = 'info_nav_' + item.id;
        const info = this.translationService.translate(infoKey);
        item.subtitle = info && info !== infoKey ? info : null;

        if (item.children?.length) {
            for (const child of item.children) {
                this.setTitleAndSubtitle(child);
            }
        }
    }

    private checkHasAccess = (userDetails: ExposedReadUserDetailsRes, item: FuseNavigationItem, parentAccess: boolean) => {
        if (!item.meta?.roles && !item.children && !item.meta?.additionalAccessCheckers && item.link && parentAccess) {
            return true;
        }

        if (!item.meta?.roles && !item.meta?.additionalAccessCheckers && !item.link && item.children) {
            return false;
        }

        let hasAccess = !item.meta?.roles || this.hasAnyRole(userDetails, item.meta?.roles);

        // Skip this check if the role check failed
        if (hasAccess && Array.isArray(item.meta?.additionalAccessCheckers) && item.meta.additionalAccessCheckers.length) {
            for (let checker of item.meta.additionalAccessCheckers) {
                const fn = (checker as any)?.accessCheckerFunc;
                const args = (checker as any)?.args;
                if (typeof fn !== 'function') {
                    continue;
                }
                if (!fn(userDetails, args)) {
                    hasAccess = false;
                    break;
                }
            }
        }

        return hasAccess;
    };

    public inRole(user: ExposedReadUserDetailsRes | null, role: string): boolean {
        return !!user?.roles?.some((x) => x.roleName == role);
    }

    // Checks if a user has any of the roles provided in the roles parameter
    public hasAnyRole(user, roles: string[]) {
        try {
            return user.roles.some((x) => roles.includes(x.roleName));
        } catch {
            return false;
        }
    }

    public publicAuthRequest(req: ExposedAuthRequestReq): Observable<ExposedAuthRequestRes> {
        let subject = new Subject<ExposedAuthRequestRes>();
        this.http
            .post(environment.localApi + 'Auth/publicAuthRequest', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedAuthRequestRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    this.alertsService.changealertHeader('Auðkenning mistókst');
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {});
        return subject.asObservable();
    }

    public getPublicAuthRequest(): Observable<Array<ListAudkenningarLogsRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki auðkenningarbeiðnir');
        let subject = new Subject<Array<ListAudkenningarLogsRes>>();
        this.http
            .get(environment.localApi + 'Auth/getPublicAuthRequest', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListAudkenningarLogsRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listEmployees(
        cursor: string = null,
        search: string = '',
        showLoader: boolean = true
    ): Observable<ListPagedRes<ExposedListUsersRes>> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage('Sæki starfsfólk…');
        }

        let params = new HttpParams();
        if (cursor) {
            params = params.set('cursor', cursor);
        }
        if (search) {
            params = params.set('search', search);
        }

        return this.http
            .get<
                HttpStatus<ListPagedRes<ExposedListUsersRes>>
            >(environment.localApi + 'user/employees', { params, ...this.httpRequestHelper.getRequestOptionsAuth() })
            .pipe(
                map((res) => res.returnItem),
                finalize(() => showLoader && this.loaderService.changeActiveLoaders(-1))
            );
    }

    public listDepartments(showLoader = true): Observable<Array<string>> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage('Sæki upplýsingar');
        }

        let subject = new Subject<Array<string>>();
        this.http
            .get(environment.localApi + 'user/listDepartments', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<string>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    public getListUserDetails(userId: number, showLoader: boolean = false): Observable<ExposedReadListUserDetailsRes> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage('Sæki upplýsingar');
        }

        let subject = new Subject<ExposedReadListUserDetailsRes>();
        this.http
            .post(environment.localApi + 'user/userDetails', { userId }, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<ExposedReadListUserDetailsRes>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    subject.next(null);
                    console.log(error);
                },
            })
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    public updateUser(req: ExposedUpdateUserReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Vista upplýsingar notanda');
        let subject = new Subject<number>();
        this.http
            .post(environment.localApi + 'user/updateUser', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<number>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Upplýsingar notanda vistaðar');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public changeClaimInfo(kennitala: string, fullImpersonation: boolean, reason: string): Observable<any> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Kanna upplýsingar um innskráðan notanda');
        let subject = new Subject<ExposedAccountsLoginRes>();
        this.http
            .post(
                environment.localApi + 'auth/changeClaimInfo',
                { ssn: kennitala, fullImpersonation, reason },
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe(
                (res: HttpStatus<ExposedAccountsLoginRes>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(false);
                    this.alertsService.changeAlertSuccessMessage(`Notandi, ${firstname(res.returnItem.nafn)}, hefur verið valinn`);
                },
                (error) => {
                    if (error.status == 429) {
                        this.alertsService.changealertHeaderMessageFooter(
                            'Of margar fyrirspurnir sendar!',
                            'Öll misnotkun er skráð og leiðir til brottvísunar.',
                            error.error
                        );
                    }
                    subject.next(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public createUser(req: ExposedCreateUserReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Stofna notanda');
        let subject = new Subject<number>();
        this.http
            .post(environment.localApi + 'user/createUser', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<number>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Notandi hefur verið stofnaður');
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    this.alertsService.changealertHeader('Ekki tókst að skrá notanda');
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getUserAccessRoles(): Promise<ExposedListRolesRes> {
        this.loaderService.changeActiveLoaders(1);
        return new Promise<ExposedListRolesRes>((resolve, reject) => {
            this.http
                .get(environment.localApi + 'auth/getAllRoles', this.httpRequestHelper.getRequestOptionsAuth())
                .subscribe(
                    (res: HttpStatus<ExposedListRolesRes>) => {
                        resolve(res.returnItem);
                    },
                    (error) => {
                        reject(error);
                    }
                )
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public getServiceUsersAccessRoles(): Promise<ExposedListRolesRes> {
        this.loaderService.changeActiveLoaders(1);
        return new Promise<ExposedListRolesRes>((resolve, reject) => {
            this.http
                .get(environment.localApi + 'auth/getAllServiceUsersRoles', this.httpRequestHelper.getRequestOptionsAuth())
                .subscribe(
                    (res: HttpStatus<ExposedListRolesRes>) => {
                        resolve(res.returnItem);
                    },
                    (error) => {
                        reject(error);
                    }
                )
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public getEmployeeDivisions(): Promise<ExposedListEmployeeDivisionsRes[]> {
        this.loaderService.changeActiveLoaders(1);
        return new Promise<ExposedListEmployeeDivisionsRes[]>((resolve, reject) => {
            this.http
                .get(environment.localApi + 'user/employeeDivisions', this.httpRequestHelper.getRequestOptionsAuth())
                .subscribe(
                    (res: HttpStatus<ExposedListEmployeeDivisionsRes[]>) => {
                        resolve(res.returnItem);
                    },
                    (error) => {
                        reject(error);
                    }
                )
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }
}
